import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, hotTopics, featuredTags } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const featuredArticles = articles.filter((a) => a.isFeatured).slice(0, 6);
const recentArticles = articles.filter((a) => !a.isFeatured).slice(0, 6);

export default function Home() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(20px)';
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '51CG吃瓜特色',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${typeof window !== 'undefined' ? window.location.origin : ''}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51CG吃瓜特色',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    logo: `${typeof window !== 'undefined' ? window.location.origin : ''}/logo.png`,
    description: '汇集最新热门瓜料、明星黑料、娱乐爆料与网友热议话题的综合性吃瓜导航与深度解析平台。',
    sameAs: ['http://leonqt.com/'],
  };

  const webPageLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '51CG吃瓜特色 - 最新热门瓜料实时更新与深度解析平台',
    url: typeof window !== 'undefined' ? window.location.href : '',
    description: '51CG.吃瓜特色是汇集最新热门瓜料、明星黑料、娱乐爆料与网友热议话题的综合性吃瓜导航与深度解析平台。',
    isPartOf: {
      '@type': 'WebSite',
      name: '51CG吃瓜特色',
      url: typeof window !== 'undefined' ? window.location.origin : '',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51CG吃瓜是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51CG吃瓜特色是汇集最新热门瓜料、明星黑料、娱乐爆料与网友热议话题的综合性吃瓜导航与深度解析平台，提供实时更新的热门吃瓜内容与深度分析。',
        },
      },
      {
        '@type': 'Question',
        name: '51CG吃瓜的内容可靠吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51CG吃瓜对所有发布内容进行初步审核，标注信息来源。热门爆料会标注爆料标签，深度分析文章会提供证据链。建议读者保持独立思考，理性吃瓜。',
        },
      },
      {
        '@type': 'Question',
        name: '如何第一时间获取最新瓜料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '建议收藏51CG吃瓜首页，关注热门吃瓜专区。重大事件通常会在首页置顶展示，分类专区也会实时更新各领域的最新动态。',
        },
      },
    ],
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin : '',
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="51CG吃瓜特色 - 最新热门瓜料实时更新与深度解析平台"
        description="51CG.吃瓜特色是汇集最新热门瓜料、明星黑料、娱乐爆料与网友热议话题的综合性吃瓜导航与深度解析平台。集成51cg及cg51访问入口，提供热门吃瓜内容与趣味莫里秀找爹系列。"
        keywords="51cg, cg51, 51cg1.ct吃瓜, 51cg莫里秀找爹, 51cg入口, 51.cg, 51cg黑料, 51cg爆料, 热门吃瓜专区, 最新瓜料资讯, 51cg吃瓜"
        ogType="website"
        jsonLd={[websiteLd, orgLd, webPageLd, faqLd, breadcrumbLd]}
      />

      {/* Hero Section */}
      <section className="relative min-h-[520px] md:min-h-[640px] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20urban%20night%20cityscape%20with%20lime%20green%20neon%20gossip%20headlines%20floating%20as%20light%20trails%2C%20dramatic%20high%20contrast%20editorial%20illustration%2C%20mysterious%20atmosphere%2C%20modern%20digital%20art%20style%2C%20entertainment%20news%20aesthetic&width=1600&height=800&seq=hero-bg-01&orientation=landscape"
            alt="51CG吃瓜特色背景"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/40" />
        </div>

        <div ref={heroRef} className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-20 md:pt-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/20 backdrop-blur-sm border border-accent-400/30 mb-5">
              <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
              <span className="text-xs font-medium text-accent-300">实时更新 · 全网追踪</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight">
              <span className="block">全网最热</span>
              <span className="block text-accent-400">瓜料情报站</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
              汇集明星黑料、娱乐爆料、深度解析与莫里秀经典，51CG吃瓜特色让您掌握第一手热门资讯，做一个称职的吃瓜群众。
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/category/hot-gossip"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-foreground-950 font-semibold text-sm hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                浏览热门吃瓜
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-white/30 text-white font-medium text-sm hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                了解51CG
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Hot Topics + Featured Grid */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-50">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Section Header */}
            <div className="flex items-end justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-sm font-bold text-foreground-500 uppercase tracking-wider mb-1">
                  Featured Stories
                </h2>
                <p className="text-2xl md:text-3xl font-bold text-foreground-950">
                  精选热门瓜料
                </p>
              </div>
              <Link
                to="/category/hot-gossip"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {/* Featured Articles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {/* Recent Articles List */}
            <div className="mt-12 md:mt-16">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="text-sm font-bold text-foreground-500 uppercase tracking-wider mb-1">
                    Latest Updates
                  </h2>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-950">
                    最新更新
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                {recentArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="horizontal" />
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0 space-y-8">
            {/* Hot Topics */}
            <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
              <h3 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500" />
                实时热榜
              </h3>
              <div className="space-y-3">
                {hotTopics.map((topic, idx) => (
                  <Link
                    key={topic.id}
                    to={`/category/hot-gossip`}
                    className="flex items-center gap-3 group"
                  >
                    <span
                      className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 ${
                        idx < 3
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1 flex-1">
                      {topic.title}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-foreground-400 flex-shrink-0">
                      {topic.trend === 'up' && <i className="ri-arrow-up-line text-red-500" />}
                      {topic.trend === 'down' && <i className="ri-arrow-down-line text-green-500" />}
                      {topic.trend === 'stable' && <i className="ri-subtract-line text-foreground-400" />}
                      {topic.heat}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Popular Tags */}
            <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
              <h3 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-accent-500" />
                热门标签
              </h3>
              <div className="flex flex-wrap gap-2">
                {featuredTags.map((tag) => (
                  <Link
                    key={tag}
                    to="/category/hot-gossip"
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-200 text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors whitespace-nowrap"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* Ad / Promo */}
            <div className="rounded-lg overflow-hidden border border-background-200/70">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20neon%20lime%20green%20glowing%20notification%20bell%20on%20dark%20charcoal%20background%2C%20subscribe%20alert%20concept%2C%20clean%20geometric%20composition%2C%20digital%20art%2C%20subtle%20gradient&width=400&height=300&seq=sidebar-promo-01&orientation=squarish"
                alt="订阅更新"
                className="w-full h-40 object-cover"
              />
              <div className="p-4 bg-background-100">
                <h4 className="text-sm font-bold text-foreground-950 mb-1">不错过任何瓜料</h4>
                <p className="text-xs text-foreground-500 mb-3">订阅51CG吃瓜，热门事件第一时间推送</p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  立即订阅 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Categories Section */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100">
        <div className="text-center mb-10 md:mb-12">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-900 border border-secondary-200 mb-3">
            分类浏览
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            五大吃瓜专区
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            从明星黑料到莫里秀经典，51CG吃瓜覆盖您想看的所有娱乐热点
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group relative rounded-lg overflow-hidden aspect-[4/5] border border-background-200/70 hover:border-accent-300 transition-all"
            >
              <img
                src={cat.image}
                alt={cat.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-accent-500 text-foreground-950 mb-2">
                  {cat.articleCount} 篇
                </span>
                <h3 className="text-lg md:text-xl font-bold text-white mb-1">
                  {cat.name}
                </h3>
                <p className="text-xs text-white/70 line-clamp-2">
                  {cat.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Platform Features */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-50">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-900 border border-secondary-200 mb-3">
            为什么选择51CG
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            平台核心优势
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            专注吃瓜多年，我们深知什么才是真正值得您关注的内容
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {[
            {
              icon: 'ri-flashlight-line',
              title: '实时更新',
              desc: '7x24小时追踪全网热点，确保您在第一时间获取最新瓜料资讯',
            },
            {
              icon: 'ri-shield-check-line',
              title: '独家爆料',
              desc: '与圈内人士建立深度联系，为您带来别处看不到的一手内幕消息',
            },
            {
              icon: 'ri-search-line',
              title: '深度解析',
              desc: '不只是吃瓜，更有专业团队对热点事件进行多维度深度分析与真相还原',
            },
            {
              icon: 'ri-community-line',
              title: '网友热议',
              desc: '汇集全网最精彩的网友评论与观点碰撞，吃瓜也能看到多元视角',
            },
          ].map((feat) => (
            <div
              key={feat.title}
              className="group p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:bg-accent-50 hover:border-accent-200 transition-all"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-accent-100 transition-colors mb-4">
                <i className={`${feat.icon} text-lg md:text-xl text-accent-600`} />
              </div>
              <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2">
                {feat.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {feat.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials / Comments */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100">
        <div className="mb-10 md:mb-12">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-900 border border-secondary-200 mb-3">
            网友怎么说
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950">
            吃瓜群众真实评价
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {[
            {
              name: '爱吃瓜的猫',
              handle: '@melon_cat',
              content: '每天上班第一件事就是打开51CG，看看昨晚娱乐圈又发生了什么惊天大事。更新速度真的快，比其他平台快多了！',
              color: 'bg-primary-100 text-primary-700',
            },
            {
              name: '娱乐圈福尔摩斯',
              handle: '@detective_ent',
              content: '深度解析板块做得特别好，不是那种哗众取宠的标题党，而是真的有逻辑、有证据的分析。莫里秀系列也是我的最爱！',
              color: 'bg-accent-100 text-accent-700',
            },
            {
              name: '理性吃瓜人',
              handle: '@rational_melon',
              content: '作为一个理性吃瓜群众，我很欣赏51CG在爆料的同时也会标注信息来源，让我们能自己判断可信度。这才是专业的吃瓜平台。',
              color: 'bg-secondary-100 text-secondary-700',
            },
            {
              name: '追星少女小李',
              handle: '@star_chaser_li',
              content: '自从关注了51CG，我在闺蜜群里的八卦消息永远是最新的。热门吃瓜专区简直就是我的快乐源泉！',
              color: 'bg-primary-100 text-primary-700',
            },
            {
              name: '前娱记老王',
              handle: '@former_reporter_wang',
              content: '做了十几年娱记，我可以负责任地说，51CG的独家爆料很多确实是圈内一手消息。这个平台背后肯定有资深业内人士支持。',
              color: 'bg-accent-100 text-accent-700',
            },
            {
              name: '深夜刷手机',
              handle: '@night_scroll',
              content: '最喜欢的是热榜功能，一目了然看到当前最火的话题。界面也很清爽，没有乱七八糟的广告，阅读体验很棒。',
              color: 'bg-secondary-100 text-secondary-700',
            },
          ].map((item, idx) => (
            <div key={idx} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${item.color}`}>
                  {item.name[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{item.name}</p>
                  <p className="text-xs text-foreground-500">{item.handle}</p>
                </div>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed">
                &ldquo;{item.content}&rdquo;
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-50">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            不错过任何一颗瓜
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-8">
            关注51CG吃瓜特色，让热门资讯主动找上门。无论身在何处，精彩从不缺席。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              立即关注我们
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/category/hot-gossip"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-foreground-300 text-foreground-700 font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              浏览热门内容
            </Link>
          </div>
        </div>

        {/* Bottom banner image */}
        <div className="mt-10 rounded-xl overflow-hidden border border-background-200/70">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20abstract%20collage%20of%20gossip%20headlines%2C%20social%20media%20icons%2C%20emoji%20faces%20and%20lime%20green%20neon%20elements%20floating%20on%20light%20cream%20background%2C%20entertainment%20news%20mosaic%20style%2C%20modern%20digital%20art%2C%20playful%20composition&width=1600&height=300&seq=cta-banner-01&orientation=landscape"
            alt="51CG吃瓜特色"
            className="w-full h-40 md:h-56 object-cover"
          />
        </div>
      </section>
    </>
  );
}
